<template>
  <sections-extra-layout>
    <template slot="title">General</template>
    <template slot="description">Define the global information of the {{ type }}.</template>
    <template slot="content">
      <div>
        <div class="flex">
          <div class="w-full md:w-1/4 mr-2">
            <span class="font-bold">Do you want to enable this {{ type }} ?</span>
          </div>
          <div
            @click="checkToggle"
            class="mb-4 w-14 h-7 cursor-pointer flex items-center rounded-full mx-2 px-1"
            :class="{ 'bg-gray-300': validateToggle, 'bg-green-700': !validateToggle }"
          >
            <div
              class="bg-white w-5 h-5 rounded-full shadow-md"
              :class="{ 'transform-none': validateToggle, 'transform translate-x-7 mr-5': !validateToggle }"
            ></div>
          </div>
        </div>
        <div class="mb-2 md:flex items-center">
          <div class="w-full md:w-1/4 mb-4 mr-2">
            <span class="font-bold">What is the name of this {{ type }}?</span>
            <span class="italic text-gray-500">(required)</span>
          </div>

          <ValidationProvider
            mode="lazy"
            rules="required|min:4"
            v-slot="{ errors }"
            class="w-full md:w-3/4 mb-4"
            name="name"
          >
            <input
              v-model="fields.name"
              class="p-3 placeholder-gray-400 border border-gray-300 text-gray-700 relative bg-white bg-white rounded text-sm outline-none focus:outline-none focus:shadow-outline w-full"
            />
            <span class="text-red-500 font-bold m-4">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="mb-2 md:flex items-center">
          <div class="w-full md:w-1/4 mb-4 mr-2">
            <span class="font-bold">What's the activation period ?</span>
            <span class="italic text-gray-500">({{ currentSite.timezone }})</span>
          </div>
          <div class="w-full md:w-3/4 mb-4 flex justify-between items-center">
            <el-date-picker
              v-model="fields.activationdate"
              format="yyyy-MM-dd"
              type="date"
              placeholder="Date de début"
              :picker-options="pickerOptionsStart"
            >
            </el-date-picker>

            <span>-</span>

            <el-date-picker
              v-model="fields.deactivationdate"
              format="yyyy-MM-dd"
              type="date"
              placeholder="Date de fin"
              :picker-options="pickerOptionsEnd"
            >
            </el-date-picker>
          </div>
        </div>

        <div class="flex items-center">
          <div class="mr-2 md:w-1/4">
            <span class="font-bold">Code</span>
          </div>

          <div class="md:w-3/4">
            <div class="fields-container">
              <div class="relative">
                <button
                  class="rounded bg-gray-200 button-field font-semibold"
                  v-on:click="
                    e => {
                      copyFieldToClipBoard(e, code)
                    }
                  "
                >
                  {{ code }}
                </button>
                <div class="copy-popin">Copied !</div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex">
          <div class="md:mr-2 md:w-1/4">&nbsp;</div>
          <div class="md:w-3/4">
            <p class="italic text-gray-400 mt-2">
              To test the {{ type }} on your website, you juste need to send and header X-{{
                _.startCase(_.toLower(type))
              }}-Code with this code as the value. To setup the header you can use
              <a href="https://bewisse.com/modheader/" target="_blank">ModHeader extension</a>. You can send the X-Lang
              header too if you want to test the {{ type }} on an other lang.
            </p>
          </div>
        </div>

        <div v-if="type === 'banner' && bannerStats !== null" class="mt-5 flex">
          <div class="mr-2 md:w-1/4">
            <span class="font-bold">Statistics</span>
          </div>

          <div class="flex items-center w-3/4">
            <div class="pl-1 md:w-full flex gap-2" style="flex-flow:row wrap;">
              <div>
                <p class="font-bold">Total view :</p>
                <p class="font-bold">Total unique view :</p>
                <p class="font-bold">Total link pressed :</p>
                <p class="font-bold">Total unique link pressed :</p>
              </div>
              <div>
                <p>{{ bannerStats.totalViews }}</p>
                <p>{{ bannerStats.uniqueViews }}</p>
                <p>{{ bannerStats.totalLinkPressed }}</p>
                <p>{{ bannerStats.uniqueLinkPressed }}</p>
              </div>
            </div>

            <common-button
              @click="
                () => {
                  this.openPopup()
                  this.isOpenPopup = true
                }
              "
              >View Stats
            </common-button>
          </div>
          <banners-ui-modal-stats
            v-if="isOpenPopup"
            :open="isOpenPopup"
            :site="site"
            :close="closePopup"
            :id="bannerId"
          />
        </div>

        <div v-if="isCsv" class="flex my-4">
          <div class="mr-2 md:w-1/4">
            <span class="font-bold">CSV</span>
          </div>

          <div class="md:w-3/4">
            <div class="flex flex-wrap gap-x-4 gap-y-2">
              <div class="flex items-center">
                <strong>Enable fields :</strong>
              </div>
              <span
                class="rounded bg-gray-200 py-1 px-2 font-semibold"
                v-for="(field, index) in csvFields"
                :key="index"
                >{{ field }}</span
              >
            </div>
            <p class="my-2"><strong>Update csv file :</strong></p>
            <div class="flex items-center gap-4">
              <common-input-file @change="handleImportCsvFile" accept=".csv">
                <span class="box-border px-4 py-2 rounded bg-green-500 text-white max-w-max shadow-sm hover:shadow-md"
                  >Choose a file</span
                >
              </common-input-file>
              <span v-if="importCsv"
                >the file <strong>{{ importCsv.name }}</strong> will be sent</span
              >
              <button class="text-red-500" v-if="importCsv" @click="removeCsvFile" type="secondary">
                remove
              </button>
              <button
                v-if="importCsv"
                v-on:click="$emit('upload-csv-file')"
                class="loader-button box-border px-4 py-2 rounded bg-yellow-500 text-white max-w-max shadow-sm hover:shadow-md"
              >
                <img v-if="loading" src="../../../../assets/img/loading.gif" alt="Loading" />
                upload
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </sections-extra-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'General',
  props: {
    name: {
      type: String,
      required: true
    },
    activationdate: {
      type: String,
      required: false
    },
    deactivationdate: {
      type: String,
      required: false
    },
    code: {
      type: String,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    status: {
      type: String,
      required: true,
      default: 'disable'
    },
    site: {
      type: Object,
      required: false
    },
    bannerId: {
      type: String,
      required: false
    },
    csvFields: {
      type: Array,
      default() {
        return []
      }
    },
    importCsv: {
      default: null
    },
    isCsv: {
      type: Boolean,
      default: false,
      required: false
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      bannerStats: null,
      isOpenPopup: false,

      fields: {
        name: null,
        activationdate: null,
        deactivationdate: null,
        status: null
      },

      date: {
        activationTimestamp: null,
        deactivationTimestamp: null
      },

      pickerOptionsStart: {
        disabledDate: time => {
          if (this.fields.deactivationdate) {
            return time.getTime() > new Date(this.fields.deactivationdate).getTime()
          }
          return false
        }
      },

      pickerOptionsEnd: {
        disabledDate: time => {
          if (this.fields.activationdate) {
            return time.getTime() < new Date(this.fields.activationdate).getTime()
          }
          return false
        }
      }
    }
  },
  computed: {
    ...mapGetters('account', {
      currentSite: 'currentSite'
    }),
    validateToggle() {
      return this.fields.status !== 'enable'
    }
  },
  watch: {
    'fields.activationdate': function(date) {
      date = this._setDate(date, this.currentSite.timezone, true)
      if (date === 'Invalid dateZ') {
        date = null
      }
      this.$emit('update-activationdate', date)
    },
    'fields.deactivationdate': function(date) {
      date = this._setDate(date, this.currentSite.timezone, false)
      if (date === 'Invalid dateZ') {
        date = null
      }
      this.$emit('update-deactivationdate', date)
    },
    'fields.name': function(name) {
      this.$emit('update-name', name)
    }
  },
  methods: {
    ...mapActions('banners', ['getBannerStats']),

    copyFieldToClipBoard(e, field) {
      navigator.clipboard.writeText(field)
      e.target.classList.add('active')
      setTimeout(() => {
        e.target.classList.remove('active')
      }, 1000)
    },

    checkToggle() {
      this.fields.status === 'disable' ? (this.fields.status = 'enable') : (this.fields.status = 'disable')

      this.$emit('update-status', this.fields.status)
    },
    getBannerStatistics() {
      this.getBannerStats({ id: this.bannerId }).then(data => {
        this.bannerStats = {
          totalViews: (data.results.views.total ? data.results.views.total : 0).toString(),
          uniqueViews: (data.results.views.unique ? data.results.views.unique : 0).toString(),
          totalLinkPressed: (data.results.link_pressed.total ? data.results.link_pressed.total : 0).toString(),
          uniqueLinkPressed: (data.results.link_pressed.unique ? data.results.link_pressed.unique : 0).toString()
        }
      })
    },
    openPopup() {
      this.isOpenPopup = true
      this.$forceUpdate()
    },
    closePopup() {
      this.isOpenPopup = false
      this.$forceUpdate()
    },
    handleImportCsvFile(e) {
      this.$emit('update-csv-import', e.target.files[0])
    },
    removeCsvFile() {
      this.$emit('update-csv-import', null)
    }
  },
  mounted() {
    this.fields.name = this.name
    this.fields.status = this.status ? this.status : 'disable'
    if (this.activationdate && this.deactivationdate) {
      this.fields.activationdate = this._getDate(this.activationdate, this.currentSite.timezone)
      this.fields.deactivationdate = this._getDate(this.deactivationdate, this.currentSite.timezone)
    }
    this.fields.status = this.status
    if (this.type === 'banner') {
      this.getBannerStatistics()
    }
  }
}
</script>

<style>
.el-date-editor.el-input,
.el-date-editor.el-input__inner {
  width: 46%;
}

.fields-container {
  display: flex;
  gap: 10px 16px;
  flex-flow: row wrap;
}

.button-field {
  padding: 4px 8px;
}

.copy-popin {
  display: none;
  z-index: 10;
  position: absolute;
  top: -36px;
  left: 50%;
  width: 70px;
  box-sizing: border-box;
  margin-left: -35px;
  padding: 4px 8px;
  text-align: center;
  border-radius: 0.25rem;
  background-color: #f59e0b;
  color: #fff;
}

.button-field.active ~ .copy-popin {
  display: block;
  animation: 1s linear pop;
  transform: scale(0);
}

@keyframes pop {
  from {
    transform: scale(0.5);
  }
  10% {
    transform: scale(1);
  }
  90% {
    transform: scale(1);
  }
  to {
    transform: scale(0.5);
  }
}

.loader-button {
  display: flex;
  align-items: center;
}

.loader-button img {
  margin-right: 8px;
}
</style>



// WEBPACK FOOTER //
// src/components/common/ui/form/general.vue